import React, { useEffect, useContext, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import i18n from 'i18n-js';
import moment from 'moment';
import Can from 'components/Can';
import { Card } from 'components/UI';
import { UserContext } from 'context';
import { FETCH_STATE } from 'constants';
import Loading from 'components/Loading';
import { debounce, isEmpty } from 'lodash';
import Paginator from 'components/Paginator';
import OrgAbility from 'components/OrgAbility';
import { CalendarTimeline } from 'components/UI';
import SearchIcon from '@mui/icons-material/Search';
import SelectedItemPopup from './SelectedItemPopup';
import { SearchField } from 'components/FormFields';
import { Box, Grid, Typography } from '@mui/material';
import { forceTimezone, buildUrl, canAccess } from 'utils';
import { DashboardActions } from 'state/modules/dashboard';
import { ReservationStatusSelect } from 'components/DataFields';
import { VehicleGroupsTimezonesSelect } from 'components/AsyncFields';
import { formatUsageData, getAllReservationsFromUsages } from 'services/noc';
import { ChangeTimesReservationModal, ChangeVehicleReservationModal } from 'components/Modals';

const CalendarWidget = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    time_zone: moment.tz.guess(),
    keyword: [],
  });
  const [selectedItem, setSelectedItem] = useState({});
  const [currentTz, setCurrentTz] = useState(moment.tz.guess());
  const [currentReservation, setCurrentReservation] = useState(false);
  const [openChangeVehicleModal, setOpenChangeVehicleModal] = useState(false);
  const [openUpdateTimesReservationModal, setOpenUpdateTimesReservationModal] = useState(false);

  const config = useSelector((state) => state.config?.settings);
  const dashboardSettings = config?.integrations?.dashboard_settings?.noc || {};
  const requestType = config?.request_type;
  const endDate = useSelector((state) => state.dashboard.endDate);
  const startDate = useSelector((state) => state.dashboard.startDate);
  const fetchingVehiclesUsages = useSelector((state) => state.dashboard.fetchingVehiclesUsages);
  const fetchedReservationSuccess = useSelector(
    (state) => state.reservations.fetchedReservationSuccess
  );
  const data = useSelector((state) => state.dashboard?.vehiclesUsages?.data);
  const pagination = useSelector((state) => state.dashboard?.vehiclesUsages?.pagination);
  const reservations = useMemo(
    () => getAllReservationsFromUsages(data, currentTz) || [],
    [data, currentTz]
  );
  const usages = useMemo(() => formatUsageData(data, currentTz) || [], [data, currentTz]);
  const items = usages?.items;
  const isLoading = fetchingVehiclesUsages === FETCH_STATE.LOADING;
  const noData = !data;

  const { orgType } = useContext(UserContext);
  const handleOpenChangeVehicleModal = () => setOpenChangeVehicleModal((prevState) => !prevState);
  const handleOpenUpdateTimesReservationModal = () =>
    setOpenUpdateTimesReservationModal((prevState) => !prevState);

  /**
   * Get usage dates
   */
  const getUsageDates = () => {
    let daysAtStart = dashboardSettings?.usage?.days_at_start || 2;
    let daysAtEnd = dashboardSettings?.usage?.days_at_end || 2;
    const initDate = startDate || moment().startOf('day').subtract(daysAtStart, 'days');
    const lastDate = endDate || moment().endOf('day').add(daysAtEnd, 'days');
    return { startDate: initDate, endDate: lastDate };
  };

  /**
   * Close context menu with selected item
   */
  const closeContextMenu = () => {
    setSelectedItem(null);
  };

  /**
   * Get vehicles groups usages
   */
  const loadVehiclesUsages = (newParams, startDate, endDate) => {
    const payload = {
      ...newParams,
      end_time: forceTimezone(endDate).tz(currentTz).format('YYYY-MM-DDTHH:mm:00Z'),
      start_time: forceTimezone(startDate).tz(currentTz).format('YYYY-MM-DDTHH:mm:00Z'),
    };

    if (startDate !== null && endDate !== null) {
      dispatch(DashboardActions.changeNocCalendarDates({ startDate, endDate }));
    }

    if (payload.vehicle_group_id) {
      delete payload.parent_id;
    }

    if (!isEmpty(payload.keyword)) {
      // We transform the keyword object to an array
      payload.keyword = Object.keys(payload?.keyword)
        .map((k) => (payload?.keyword[k] ? `${k}:${payload?.keyword[k]}` : ''))
        .filter((i) => i !== '');
    }

    if (isEmpty(payload.keyword)) {
      delete payload.keyword;
    }

    dispatch(DashboardActions.fetchVehicleUsages(payload, currentTz));
  };

  /**
   * Search by vehicle
   */
  const onSearch = useCallback(
    debounce((name, value) => {
      const keyword = {
        ...params?.keyword,
      };
      if (name !== 'time_zone') {
        keyword[name] = value;
      }
      let filters = {
        ...params,
        [name]: name === 'time_zone' ? value : undefined,
        keyword,
        page: 1,
      };

      if (name === 'reservation_status') filters['reservation_status'] = value;
      Object.keys(filters).forEach(
        (key) => key !== 'keyword' && !filters[key] && delete filters[key]
      );
      setParams({ ...filters });

      const { startDate, endDate } = getUsageDates();
      loadVehiclesUsages(filters, startDate, endDate);
    }, 1000)
  );

  /**
   * Render filters
   */
  const renderSubheaderFilters = () => {
    return (
      <Box sx={{ minHeight: 75, padding: '24px 32px' }}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Grid
              container
              wrap="nowrap"
              rowSpacing={1}
              alignItems="flex-end"
              columnSpacing={{ xs: 1 }}
            >
              <Grid>
                <SearchIcon sx={{ color: 'text.primary', fontSize: 16 }} />
              </Grid>
              <Grid style={{ width: '100%' }}>
                <SearchField
                  allowClear
                  margin="none"
                  label={i18n.t(
                    `dashboard.${
                      orgType !== 'msOwnerOrg' ? 'search_vehicle_group' : 'search_service_area'
                    }`
                  )}
                  field={{
                    onChange: onSearch,
                    name: 'vehicle_group',
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          {orgType !== 'msOwnerOrg' && (
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <Grid
                container
                wrap="nowrap"
                rowSpacing={1}
                alignItems="flex-end"
                columnSpacing={{ xs: 1 }}
              >
                <Grid>
                  <SearchIcon sx={{ color: 'text.primary', fontSize: 16 }} />
                </Grid>
                <Grid style={{ width: '100%' }}>
                  <SearchField
                    allowClear
                    margin="none"
                    label={i18n.t('dashboard.search_location')}
                    field={{
                      onChange: onSearch,
                      name: 'location',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Grid
              container
              wrap="nowrap"
              rowSpacing={1}
              alignItems="flex-end"
              columnSpacing={{ xs: 1 }}
            >
              <Grid>
                <SearchIcon sx={{ color: 'text.primary', fontSize: 16 }} />
              </Grid>
              <Grid style={{ width: '100%' }}>
                <SearchField
                  allowClear
                  margin="none"
                  label={i18n.t('dashboard.search_vehicle')}
                  data-cy="vehicle-search-input"
                  field={{
                    onChange: onSearch,
                    name: 'vehicle',
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <OrgAbility subject={['msServiceOrg']}>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <Grid
                container
                wrap="nowrap"
                rowSpacing={1}
                alignItems="flex-end"
                columnSpacing={{ xs: 1 }}
              >
                <Grid>
                  <SearchIcon sx={{ color: 'text.primary', fontSize: 16 }} />
                </Grid>
                <Grid style={{ width: '100%' }}>
                  <SearchField
                    allowClear
                    margin="none"
                    label={i18n.t('dashboard.search_user')}
                    field={{
                      onChange: onSearch,
                      name: 'user',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </OrgAbility>
          <Can I="read" on="vehicle_group">
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <VehicleGroupsTimezonesSelect
                margin="none"
                form={{
                  setFieldValue: (field, value) => setCurrentTz(value || moment.tz.guess()),
                }}
                field={{
                  onChange: onSearch,
                  name: 'time_zone',
                  value: currentTz || '',
                }}
                label={i18n.t('dashboard.time_zone')}
              />
            </Grid>
          </Can>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Grid
              container
              wrap="nowrap"
              rowSpacing={1}
              alignItems="flex-end"
              columnSpacing={{ xs: 1 }}
            >
              <Grid>
                <SearchIcon sx={{ color: 'text.primary', fontSize: 16 }} />
              </Grid>
              <Grid style={{ width: '100%' }}>
                <ReservationStatusSelect
                  field={{
                    name: 'reservation_status',
                    value: params.reservation_status || '',
                  }}
                  omitFields={['ADMINCANCELLED', 'USERCANCELLED', 'REJECTED', 'PENDING']}
                  margin="none"
                  form={{ setFieldValue: onSearch }}
                  label={i18n.t('dashboard.search_reservation_status')}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  };

  /**
   * Handle item click
   * @param {number} id
   * @param {object} event
   */
  const onTimelineClickHandler = (id, event) => {
    const item = reservations.find((val) => val.key === id);
    if (id === selectedItem?.item?.key) {
      return closeContextMenu();
    }
    if (item) {
      if (item.status !== 'OFFLINE' && canAccess(item)) {
        setSelectedItem({
          item,
          open: true,
          menuPosition: {
            x: event.clientX,
            y: event.clientY,
          },
        });
      }
    }
  };

  /**
   * Handle noc item double click
   * @param {number} id Id of the selected item
   */
  const onItemDoubleClick = (id) => {
    const item = reservations.find((val) => val.id === id);
    if (item) {
      history.push(buildUrl(`/reservations/${id}`));
    }
  };

  /**
   * Handle go to reservation
   * @param {number} id Id of the selected reservation
   */
  const handleChangeReservationTimes = (item) => {
    setCurrentReservation(item);
    handleOpenUpdateTimesReservationModal();
  };

  /**
   * Handle go to reservation
   * @param {number} id Id of the selected reservation
   */
  const handleChangeVehicleOnReservation = (item) => {
    setCurrentReservation(item);
    handleOpenChangeVehicleModal();
  };

  /**
   * Handle go to reservation
   * @param {number} id Id of the selected reservation
   */
  const goToReservation = (item) => {
    if (item.status !== 'OFFLINE' && canAccess(item)) {
      history.push(buildUrl(`/reservations/${item.id}`));
    }
  };

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handleChangePage = (event, newPage) => {
    const { startDate, endDate } = getUsageDates();
    const newParams = {
      ...params,
      page: newPage + 1,
    };
    setParams(newParams);
    loadVehiclesUsages(newParams, startDate, endDate);
  };

  const handleChangeRowsPerPage = (event) => {
    const { startDate, endDate } = getUsageDates();
    const newParams = {
      ...params,
      limit: event.target.value,
    };
    setParams(newParams);
    loadVehiclesUsages(newParams, startDate, endDate);
  };

  /**
   * Handle timeline refresh
   */
  const onRefresh = (startTime, endTime, filters) => {
    const { startDate, endDate } = getUsageDates();
    loadVehiclesUsages(filters || params, startTime || startDate.utc(), endTime || endDate.utc());
  };

  /**
   * Handle scroll event
   */
  const handleScroll = () => {
    closeContextMenu();
  };

  /**
   * Handle on key press event
   */
  const handleKeyPress = (event) => {
    if (event.keyCode === 27) {
      //Esc Key
      closeContextMenu();
    }
  };

  useEffect(() => {
    if (orgType) {
      onRefresh();
    }
  }, [orgType]); // eslint-disable-line

  useEffect(() => {
    if (fetchedReservationSuccess) {
      onRefresh();
    }
  }, [fetchedReservationSuccess]); // eslint-disable-line

  // Add event listener to handle scroll/keydown on load, and remove them on unmount
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('keydown', handleKeyPress, true);
    };
  }, [handleKeyPress, handleScroll]);

  const handleRefresh = useCallback(
    debounce((params, visibleTimeStart, visibleTimeEnd) => {
      onRefresh(moment(visibleTimeStart), moment(visibleTimeEnd), params);
    }, 1000),
    [onRefresh, params]
  );

  const handleTimeChange = (visibleDatetimeStart, visibleDatetimeEnd, updateScrollCanvas) => {
    updateScrollCanvas && updateScrollCanvas(visibleDatetimeStart, visibleDatetimeEnd);
    handleRefresh(params, visibleDatetimeStart, visibleDatetimeEnd);
  };

  return (
    <Card
      enableCollapse
      id="noc-calendar"
      data-cy="calendar"
      headerActionType="vertical"
      onRefresh={() => onRefresh()}
      loading={isLoading}
      subheader={renderSubheaderFilters}
      title={i18n.t('dashboard.noc_calendar_title')}
    >
      <Box minHeight={240}>
        <Box mb="5px">
          {noData ? (
            <Box m={0} p={2} display="flex" alignItems="center" justifyContent="center">
              <Loading />
            </Box>
          ) : (
            <>
              <CalendarTimeline
                org={orgType}
                items={items}
                tz={currentTz}
                endDate={endDate}
                startDate={startDate}
                groups={usages?.groups}
                onTimeChange={handleTimeChange}
                filteredItem={params.nocVehicleId}
                settings={dashboardSettings.timeline}
                onItemDoubleClick={onItemDoubleClick}
                onTimelineClickHandler={onTimelineClickHandler}
              />
              {data?.length === 0 ? (
                <Typography variant="h4" align="center" data-cy="timeline-no-data">
                  <Box fontSize={12} py={3}>
                    {i18n.t('common.no_records_to_display')}
                  </Box>
                </Typography>
              ) : (
                <Paginator
                  component="div"
                  limit={params.limit}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  pagination={pagination}
                  rowsPerPageOptions={[10, 15, 20, 25, 30]}
                />
              )}
            </>
          )}
        </Box>
        {selectedItem?.open && (
          <SelectedItemPopup
            item={selectedItem.item}
            requestType={requestType}
            handleClose={closeContextMenu}
            goToReservation={goToReservation}
            position={selectedItem.menuPosition}
            handleChangeReservationTimes={handleChangeReservationTimes}
            handleChangeVehicleOnReservation={handleChangeVehicleOnReservation}
          />
        )}
        {openChangeVehicleModal && (
          <Can I="update_vehicle" on="reservation">
            <ChangeVehicleReservationModal
              id={currentReservation?.id}
              reservationUserId={currentReservation?.user?.id}
              open={openChangeVehicleModal}
              handleClick={handleOpenChangeVehicleModal}
              vehicleId={currentReservation?.vehicle?.id}
              vehicleGroup={currentReservation?.vehicleGroup}
            />
          </Can>
        )}
        {openUpdateTimesReservationModal && (
          <Can I="update_times" on="reservation">
            <ChangeTimesReservationModal
              id={currentReservation?.id}
              reservation={currentReservation}
              open={openUpdateTimesReservationModal}
              handleClick={handleOpenUpdateTimesReservationModal}
              timezone={currentReservation?.time_zone || currentTz}
            />
          </Can>
        )}
      </Box>
    </Card>
  );
};

export { CalendarWidget };
